import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  HolderOutlined,
  ImportOutlined,
  OrderedListOutlined,
  PlusOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { CsvImportModal } from './components/CsvImportModal';
import { SizeStockField } from './components/SizeStockField';
import { MultiImageField } from './components/MultiImageField';

const LOW_STOCK_THRESHOLD = 5;
import {
  ProductCategory,
  ProductDto,
  useCreateProduct,
  useDeleteProduct,
  useProducts,
  useReorderProducts,
  useUpdateProduct,
} from '@shared/api/hooks/useProducts';
import { useTenantAdminConfig } from '@shared/api/hooks/useTenantAdminConfig';
import { normalizeShopTaxonomy } from '../settings/ShopTaxonomyTab';

const CATEGORY_LABELS: Record<ProductCategory, string> = {
  JERSEY: 'Maglia',
  SCARF: 'Sciarpa',
  HAT: 'Cappello',
  ACCESSORY: 'Accessorio',
  OTHER: 'Altro',
};

interface SizeRow {
  size: string;
  stock: number;
}

/**
 * Legge in modo sicuro una chiave (it/en) da un JSON localizzato. `name` e
 * `description` sono `Json` non-null nello schema, ma una riga legacy o mutata
 * fuori dall'app (es. script di rename) potrebbe averli null o malformati:
 * accedere a `.it` direttamente farebbe crashare l'intera pagina prodotti.
 */
function localized(value: unknown, key: 'it' | 'en'): string | undefined {
  if (value && typeof value === 'object' && key in value) {
    const v = (value as Record<string, unknown>)[key];
    if (typeof v === 'string') return v;
  }
  return undefined;
}

interface FormValues {
  nameIt: string;
  nameEn?: string;
  descIt: string;
  descEn?: string;
  price: number;
  currency: string;
  sku: string;
  category: ProductCategory;
  images: string[];
  isAvailable: boolean;
  customizable: boolean;
  allowName: boolean;
  allowNumber: boolean;
  customizationMaxLength: number;
  sizes: SizeRow[];
  // T-69 — tassonomia shop
  macrocategory?: string;
  subcategory?: string;
  sizeBand?: string;
  productLine?: string;
}

export function ProductsPage(): React.ReactElement {
  const [filterCategory, setFilterCategory] = useState<ProductCategory | undefined>(undefined);
  const [filterAvail, setFilterAvail] = useState<boolean>(true);
  const [filterLowStock, setFilterLowStock] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  // T-87 — navigazione macro → sotto (tassonomia Impostazioni Shop).
  const [filterMacro, setFilterMacro] = useState<string | undefined>(undefined);
  const [filterSub, setFilterSub] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [csvOpen, setCsvOpen] = useState(false);
  // T-113 — modalità riordino. Il riordino agisce sull'INTERO catalogo (una sola
  // sequenza display_order globale, disponibili + nascosti), quindi va fatto con
  // tutti i filtri disattivati e senza paginazione: altrimenti manderemmo al
  // backend un insieme parziale e verrebbe rifiutato (409). Per questo è una
  // modalità dedicata invece di un drag "sempre attivo" sulla lista filtrata.
  const [reorderMode, setReorderMode] = useState(false);
  const [rows, setRows] = useState<ProductDto[]>([]);
  // T-165 — errore di salvataggio riordino, MOSTRATO in modo persistente (non solo
  // come toast effimero: senza il wrapper <App> di antd il message statico può non
  // comparire, e il fallimento passava per "l'ordine non resta").
  const [reorderError, setReorderError] = useState<string | null>(null);
  const dragIndex = useRef<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const [form] = Form.useForm<FormValues>();
  const categoryWatch = Form.useWatch('category', form);
  // Pilota la visibilità dei campi personalizzazione. I campi restano SEMPRE
  // montati (display:none quando off): con `preserve={false}` smontarli
  // condizionalmente faceva perdere i valori (setFieldsValue su campo smontato
  // scartato + initialValue che sovrascrive a ogni re-render del wrapper).
  const customizableWatch = Form.useWatch('customizable', form);
  // T-69 — macro selezionata pilota le opzioni di sotto-categoria.
  const macroWatch = Form.useWatch('macrocategory', form);

  // Tassonomia shop (per i select dell'editor prodotto). Null/parziale → normalizzata.
  const { data: tenantConfig } = useTenantAdminConfig();
  const taxonomy = normalizeShopTaxonomy(tenantConfig?.shopTaxonomy);
  const subcategoryOptions = macroWatch
    ? (taxonomy.subcategoriesByMacro[macroWatch] ?? [])
    : [];

  // Navigazione shop: macro attive (le disattivate non guidano la vetrina) e
  // sotto-categorie della macro selezionata.
  const navMacros = taxonomy.macrocategories.filter(
    (m) => !(taxonomy.inactiveMacrocategories ?? []).includes(m),
  );
  const navSubOptions = filterMacro ? (taxonomy.subcategoriesByMacro[filterMacro] ?? []) : [];

  const {
    data: pageData,
    isLoading,
    refetch,
    isFetching,
  } = useProducts(
    reorderMode
      ? // Catalogo intero, nessun filtro, ordinato per display_order dal backend.
        { limit: 1000 }
      : {
          category: filterCategory,
          available: filterAvail,
          search: search || undefined,
          macrocategory: filterMacro,
          subcategory: filterSub,
          page,
          limit: 20,
        },
  );
  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct();
  const deleteMutation = useDeleteProduct();
  const reorderMutation = useReorderProducts();

  const openCreate = (): void => {
    setEditId(null);
    form.resetFields();
    form.setFieldsValue({
      currency: 'EUR',
      category: 'JERSEY',
      images: [],
      isAvailable: true,
      customizable: false,
      allowName: true,
      allowNumber: true,
      customizationMaxLength: 12,
      sizes: [],
    });
    setModalOpen(true);
  };

  const openEdit = (p: ProductDto): void => {
    setEditId(p.id);
    form.setFieldsValue({
      nameIt: localized(p.name, 'it') ?? '',
      nameEn: localized(p.name, 'en'),
      descIt: localized(p.description, 'it') ?? '',
      descEn: localized(p.description, 'en'),
      price: p.price,
      currency: p.currency,
      sku: p.sku,
      category: p.category,
      images: p.images,
      isAvailable: p.isAvailable,
      customizable: p.customizable,
      allowName: p.allowName,
      allowNumber: p.allowNumber,
      customizationMaxLength: p.customizationMaxLength,
      sizes: p.sizes,
      macrocategory: p.macrocategory ?? undefined,
      subcategory: p.subcategory ?? undefined,
      sizeBand: p.sizeBand ?? undefined,
      productLine: p.productLine ?? undefined,
    });
    setModalOpen(true);
  };

  const onSubmit = async (): Promise<void> => {
    const v = await form.validateFields();
    const images = (v.images ?? []).map((s) => s.trim()).filter(Boolean);
    if (images.length === 0) {
      message.error('Almeno un’immagine richiesta (carica un file o inserisci un URL)');
      return;
    }
    const sizes = (v.sizes ?? [])
      .filter((s) => s.size && s.size.trim().length > 0)
      .map((s) => ({ size: s.size.trim().toUpperCase(), stock: Number(s.stock) }));
    const input = {
      name: { it: v.nameIt, ...(v.nameEn ? { en: v.nameEn } : {}) },
      description: { it: v.descIt, ...(v.descEn ? { en: v.descEn } : {}) },
      price: Number(v.price),
      currency: v.currency,
      sku: v.sku,
      category: v.category,
      images,
      isAvailable: v.isAvailable,
      // T-69 — tassonomia shop (opzionali; null per svuotare)
      macrocategory: v.macrocategory || null,
      subcategory: v.subcategory || null,
      sizeBand: v.sizeBand || null,
      productLine: v.productLine?.trim() || null,
      customizable: v.customizable,
      // Inviati solo se la personalizzazione è attiva (i campi sono nascosti/smontati altrimenti).
      // Prezzi FISSI lato backend (nome +5€, numero 1 cifra +2€, 2 cifre +4€): il BO
      // configura solo COSA è offerto (nome/numero) e la lunghezza max del nome.
      ...(v.customizable
        ? {
            allowName: v.allowName,
            allowNumber: v.allowNumber,
            customizationMaxLength: Number(v.customizationMaxLength ?? 12),
          }
        : {}),
      sizes,
    };
    try {
      if (editId) {
        await updateMutation.mutateAsync({ id: editId, input });
        message.success('Prodotto aggiornato');
      } else {
        await createMutation.mutateAsync(input);
        message.success('Prodotto creato');
      }
      setModalOpen(false);
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { message?: string | string[] } } };
      const raw = err.response?.data?.message;
      const msg = Array.isArray(raw) ? raw.join('; ') : (raw ?? 'Errore di salvataggio');
      // 409 = conflitto di dato (tipicamente SKU già in uso, anche da un
      // prodotto DISATTIVATO non visibile in lista): messaggio persistente e
      // leggibile in un modal, non un toast che sparisce e che l'umano non
      // riesce a leggere (T-87).
      if (err.response?.status === 409) {
        Modal.error({ title: 'Codice prodotto (SKU) già in uso', content: msg });
      } else {
        message.error(msg);
      }
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    try {
      await deleteMutation.mutateAsync(id);
      message.success('Prodotto eliminato');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Errore eliminazione');
    }
  };

  const rawData = pageData?.data ?? [];
  const data = filterLowStock
    ? rawData.filter((p) =>
        p.sizes.some((s) => s.stock > 0 && s.stock < LOW_STOCK_THRESHOLD),
      )
    : rawData;
  const meta = pageData?.meta;
  const lowStockCount = rawData.filter((p) =>
    p.sizes.some((s) => s.stock > 0 && s.stock < LOW_STOCK_THRESHOLD),
  ).length;

  // ── T-113 riordino ────────────────────────────────────────────────────────
  // Sincronizza le righe locali dall'ordine appena persistito dal backend.
  // Dopo un commit ottimistico la mutation invalida → refetch → questo effetto
  // riallinea `rows` alla verità del server (che è identica all'ottimistico).
  useEffect(() => {
    if (reorderMode) setRows(pageData?.data ?? []);
  }, [reorderMode, pageData]);

  const enterReorder = (): void => {
    // Azzera i filtri per non lasciarli "attivi ma ignorati" sotto la modalità.
    setSearch('');
    setFilterCategory(undefined);
    setFilterMacro(undefined);
    setFilterSub(undefined);
    setFilterLowStock(false);
    setPage(1);
    setReorderError(null);
    setReorderMode(true);
  };

  const commitOrder = async (next: ProductDto[]): Promise<void> => {
    setRows(next); // ottimistico
    try {
      await reorderMutation.mutateAsync(next.map((p) => p.id));
      setReorderError(null);
      message.success('Ordine aggiornato');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      const msg = err.response?.data?.message ?? 'Riordino non riuscito. Riprova.';
      // Persistente in pagina + toast: il fallimento NON deve travestirsi da
      // "l'ordine non resta". Poi ripristiniamo l'ordine reale dal server.
      setReorderError(msg);
      message.error(msg);
      void refetch();
    }
  };

  const moveRow = (from: number, to: number): void => {
    if (from === to || from < 0 || to < 0 || from >= rows.length) return;
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    void commitOrder(next);
  };

  const handleDrop = (targetIndex: number): void => {
    const from = dragIndex.current;
    dragIndex.current = null;
    setOverIndex(null);
    if (from === null) return;
    moveRow(from, targetIndex);
  };

  const handleSetPosition = (id: string, rawPos: number): void => {
    if (!Number.isFinite(rawPos)) return;
    const from = rows.findIndex((p) => p.id === id);
    if (from === -1) return;
    const to = Math.min(Math.max(1, Math.round(rawPos)), rows.length) - 1;
    moveRow(from, to);
  };

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Prodotti shop
          </Typography.Title>
          <Tag>{meta?.total ?? data.length}</Tag>
        </Space>
      }
      extra={
        reorderMode ? (
          <Space>
            <Typography.Text type="secondary">
              Trascina le righe o modifica il numero
            </Typography.Text>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
              Aggiorna
            </Button>
            <Button type="primary" onClick={() => setReorderMode(false)}>
              Fine riordino
            </Button>
          </Space>
        ) : (
        <Space>
          <Tooltip title="Riordina l’intero catalogo (trascinamento o numero). I filtri vengono disattivati.">
            <Button icon={<OrderedListOutlined />} onClick={enterReorder}>
              Riordina
            </Button>
          </Tooltip>
          <Input.Search
            placeholder="Cerca per nome / SKU"
            allowClear
            style={{ width: 220 }}
            onSearch={(v) => {
              setSearch(v);
              setPage(1);
            }}
          />
          <Select
            placeholder="Categoria"
            style={{ width: 150 }}
            allowClear
            value={filterCategory}
            onChange={(v) => {
              setFilterCategory(v as ProductCategory | undefined);
              setPage(1);
            }}
            options={(Object.keys(CATEGORY_LABELS) as ProductCategory[]).map((c) => ({
              value: c,
              label: CATEGORY_LABELS[c],
            }))}
          />
          {navMacros.length > 0 && (
            <>
              <Select
                placeholder="Macrocategoria"
                style={{ width: 160 }}
                allowClear
                value={filterMacro}
                onChange={(v) => {
                  setFilterMacro(v as string | undefined);
                  setFilterSub(undefined); // la sotto dipende dalla macro
                  setPage(1);
                }}
                options={navMacros.map((m) => ({ value: m, label: m }))}
              />
              <Select
                placeholder="Sotto-categoria"
                style={{ width: 160 }}
                allowClear
                disabled={!filterMacro}
                value={filterSub}
                onChange={(v) => {
                  setFilterSub(v as string | undefined);
                  setPage(1);
                }}
                options={navSubOptions.map((s) => ({ value: s, label: s }))}
              />
            </>
          )}
          <Space>
            <Typography.Text type="secondary">Disp.</Typography.Text>
            <Switch
              checked={filterAvail}
              onChange={(v) => {
                setFilterAvail(v);
                setPage(1);
              }}
            />
          </Space>
          <Space>
            <Typography.Text type="secondary">
              <WarningOutlined /> Low stock {lowStockCount > 0 ? `(${lowStockCount})` : ''}
            </Typography.Text>
            <Switch
              checked={filterLowStock}
              onChange={(v) => setFilterLowStock(v)}
            />
          </Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Aggiorna
          </Button>
          <Button icon={<ImportOutlined />} onClick={() => setCsvOpen(true)}>
            Importa CSV
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nuovo prodotto
          </Button>
        </Space>
        )
      }
    >
      {reorderMode && reorderError && (
        <Alert
          type="error"
          showIcon
          closable
          onClose={() => setReorderError(null)}
          style={{ marginBottom: 12 }}
          message="Il salvataggio dell’ordine è fallito"
          description={reorderError}
        />
      )}
      {reorderMode && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Modalità riordino attiva"
          description="Stai riordinando i prodotti visibili in vetrina. Trascina una riga per la maniglia o digita la posizione nella colonna Ordine: 1 = in cima. I prodotti nascosti mantengono la loro posizione. Le modifiche vengono salvate subito. Premi “Fine riordino” per tornare alla vista filtrata."
        />
      )}
      <Table<ProductDto>
        rowKey="id"
        loading={isLoading}
        dataSource={reorderMode ? rows : data}
        scroll={{ x: 'max-content' }}
        onRow={
          reorderMode
            ? (_record, index) => ({
                draggable: true,
                onDragStart: () => {
                  dragIndex.current = index ?? null;
                },
                onDragEnter: () => setOverIndex(index ?? null),
                onDragOver: (e: React.DragEvent) => e.preventDefault(),
                onDrop: () => handleDrop(index ?? 0),
                onDragEnd: () => {
                  dragIndex.current = null;
                  setOverIndex(null);
                },
                style: {
                  cursor: 'move',
                  background:
                    overIndex === index ? 'rgba(22, 119, 255, 0.08)' : undefined,
                },
              })
            : undefined
        }
        pagination={
          reorderMode
            ? false
            : meta
            ? {
                current: meta.page,
                pageSize: meta.limit,
                total: meta.total,
                onChange: (p) => setPage(p),
                showSizeChanger: false,
              }
            : false
        }
        expandable={{
          expandedRowRender: (r) => (
            <Space wrap>
              {r.sizes.length === 0 ? (
                <Typography.Text type="secondary">Nessuna taglia</Typography.Text>
              ) : (
                r.sizes.map((s) => (
                  <Tag key={s.size} color={s.stock > 0 ? 'green' : 'red'}>
                    {s.size}: {s.stock} pz
                  </Tag>
                ))
              )}
            </Space>
          ),
          rowExpandable: () => true,
        }}
        columns={[
          {
            title: reorderMode ? 'Ordine' : '#',
            width: reorderMode ? 130 : 70,
            fixed: reorderMode ? ('left' as const) : undefined,
            render: (_v, r, index) =>
              reorderMode ? (
                <Space size={4}>
                  <HolderOutlined style={{ cursor: 'move', color: '#bbb' }} />
                  <InputNumber
                    // key con posizione: si rimonta (e riprende defaultValue)
                    // quando la riga cambia posizione dopo un drag/commit.
                    key={`ord-${r.id}-${index}`}
                    size="small"
                    min={1}
                    max={rows.length}
                    precision={0}
                    defaultValue={index + 1}
                    style={{ width: 64 }}
                    // Il click/typing nell'input non deve iniziare un drag riga.
                    draggable={false}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPressEnter={(e) =>
                      handleSetPosition(
                        r.id,
                        Number((e.target as HTMLInputElement).value),
                      )
                    }
                    onBlur={(e) => handleSetPosition(r.id, Number(e.target.value))}
                  />
                </Space>
              ) : (
                <Typography.Text type="secondary">{r.displayOrder}</Typography.Text>
              ),
          },
          {
            title: 'Foto',
            width: 80,
            render: (_v, r) =>
              r.images[0] ? (
                <img
                  src={r.images[0]}
                  alt=""
                  width={56}
                  height={40}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                />
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              ),
          },
          {
            title: 'Nome',
            render: (_v, r) => (
              <Space direction="vertical" size={0}>
                <Typography.Text strong>{localized(r.name, 'it') ?? '(senza nome)'}</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  SKU {r.sku}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: 'Categoria',
            dataIndex: 'category',
            width: 130,
            responsive: ['md'],
            render: (v: ProductCategory) => <Tag>{CATEGORY_LABELS[v]}</Tag>,
          },
          {
            title: 'Macro / Sotto',
            width: 170,
            responsive: ['lg'],
            render: (_v, r) =>
              r.macrocategory ? (
                <Space direction="vertical" size={0}>
                  <Typography.Text style={{ fontSize: 13 }}>{r.macrocategory}</Typography.Text>
                  {r.subcategory ? (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {r.subcategory}
                    </Typography.Text>
                  ) : null}
                </Space>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              ),
          },
          {
            title: 'Prezzo',
            dataIndex: 'price',
            width: 100,
            align: 'right',
            render: (v: number, r) => `${v.toFixed(2)} ${r.currency}`,
          },
          {
            title: 'Taglie',
            width: 90,
            align: 'center',
            responsive: ['md'],
            render: (_v, r) => (
              <Tag color={r.sizes.some((s) => s.stock > 0) ? 'green' : 'default'}>
                {r.sizes.length}
              </Tag>
            ),
          },
          {
            title: 'Stato',
            width: 100,
            render: (_v, r) =>
              r.isAvailable ? (
                <Tag color="green">Disponibile</Tag>
              ) : (
                <Tag color="default">Nascosto</Tag>
              ),
          },
          // Azioni nascoste in riordino: lì la riga serve solo a essere spostata.
          ...(reorderMode
            ? []
            : [
                {
                  title: '',
                  width: 100,
                  align: 'right' as const,
                  render: (_v: unknown, r: ProductDto) => (
                    <Space>
                      <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                      <Popconfirm
                        title="Eliminare prodotto?"
                        okText="Elimina"
                        cancelText="Annulla"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => onDelete(r.id)}
                      >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]),
        ]}
      />

      <Modal
        title={editId ? 'Modifica prodotto' : 'Nuovo prodotto'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={onSubmit}
        okText={editId ? 'Salva' : 'Crea'}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        destroyOnHidden
        width={760}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item
              name="nameIt"
              label="Nome (IT)"
              rules={[{ required: true, max: 200 }]}
              style={{ flex: 1, marginRight: 8 }}
            >
              <Input />
            </Form.Item>
            <Form.Item name="nameEn" label="Nome (EN)" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
          </Space.Compact>
          <Form.Item
            name="descIt"
            label="Descrizione (IT)"
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="descEn" label="Descrizione (EN)">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item
              name="sku"
              label="SKU"
              rules={[{ required: true, max: 50 }]}
              style={{ flex: 1, marginRight: 8 }}
            >
              <Input placeholder="JRS-HOME-2526" />
            </Form.Item>
            <Form.Item
              name="category"
              label="Categoria"
              rules={[{ required: true }]}
              style={{ width: 180, marginRight: 8 }}
            >
              <Select
                options={(Object.keys(CATEGORY_LABELS) as ProductCategory[]).map((c) => ({
                  value: c,
                  label: CATEGORY_LABELS[c],
                }))}
              />
            </Form.Item>
            <Form.Item
              name="price"
              label="Prezzo"
              rules={[{ required: true, type: 'number', min: 0.01 }]}
              style={{ width: 130, marginRight: 8 }}
            >
              <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="currency"
              label="Valuta"
              rules={[{ required: true, len: 3 }]}
              style={{ width: 90 }}
            >
              <Input maxLength={3} />
            </Form.Item>
          </Space.Compact>
          {/* T-69 — tassonomia catalogo (opzionale, popolata da Config tenant → Shop/Catalogo). */}
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item
              name="macrocategory"
              label="Macrocategoria"
              tooltip="Popolata da Configurazione tenant → Shop / Catalogo."
              style={{ flex: 1, marginRight: 8 }}
            >
              <Select
                allowClear
                placeholder="—"
                options={taxonomy.macrocategories.map((m) => ({ value: m, label: m }))}
                onChange={() => form.setFieldsValue({ subcategory: undefined })}
              />
            </Form.Item>
            <Form.Item
              name="subcategory"
              label="Sottocategoria"
              tooltip="Filtrata in base alla macrocategoria selezionata."
              style={{ flex: 1, marginRight: 8 }}
            >
              <Select
                allowClear
                placeholder={macroWatch ? '—' : 'Scegli prima una macrocategoria'}
                disabled={!macroWatch}
                options={subcategoryOptions.map((s) => ({ value: s, label: s }))}
              />
            </Form.Item>
            <Form.Item
              name="sizeBand"
              label="Fascia taglie"
              style={{ width: 160, marginRight: 8 }}
            >
              <Select
                allowClear
                placeholder="—"
                options={[
                  { value: 'senior', label: 'Senior' },
                  { value: 'junior', label: 'Junior' },
                ]}
              />
            </Form.Item>
            <Form.Item name="productLine" label="Linea" style={{ width: 180 }}>
              <Input allowClear placeholder="Es. 2025/26" />
            </Form.Item>
          </Space.Compact>

          <Form.Item
            name="images"
            label="Immagini prodotto (carica un file o inserisci un URL, max 10)"
            tooltip="Carica dal repository media/S3 già in uso oppure incolla un URL esterno. La prima immagine è quella principale mostrata nello shop."
            rules={[
              {
                validator: (_r, imgs: string[] | undefined) =>
                  (imgs ?? []).some((s) => s && s.trim())
                    ? Promise.resolve()
                    : Promise.reject(new Error('Almeno un’immagine richiesta')),
              },
            ]}
          >
            <MultiImageField max={10} />
          </Form.Item>
          <Form.Item
            name="isAvailable"
            label="Disponibile in shop"
            valuePropName="checked"
          >
            <Switch checkedChildren="Sì" unCheckedChildren="No" />
          </Form.Item>

          <Form.Item
            name="customizable"
            label="Personalizzazione"
            valuePropName="checked"
            tooltip="L'utente può aggiungere nome e/o numero (es. sulla maglia) con sovrapprezzo"
          >
            <Switch checkedChildren="Sì" unCheckedChildren="No" />
          </Form.Item>
          {/* Config SEMPRE montata: nascosta via display:none quando la
              personalizzazione è off. Prezzi FISSI (non configurabili): il BO
              sceglie solo cosa offrire (nome/numero) e la lunghezza max del nome. */}
          <div style={{ display: customizableWatch ? 'block' : 'none' }}>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
              Prezzi fissi: nome +5€ · numero 1 cifra +2€ · numero 2 cifre +4€. Nome e numero
              sono indipendenti e sommabili.
            </Typography.Text>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                name="allowName"
                label="Consenti nome (+5€)"
                valuePropName="checked"
                style={{ flex: 1, marginRight: 8 }}
              >
                <Switch checkedChildren="Sì" unCheckedChildren="No" />
              </Form.Item>
              <Form.Item
                name="allowNumber"
                label="Consenti numero (1 cifra +2€ / 2 cifre +4€)"
                valuePropName="checked"
                style={{ flex: 1, marginRight: 8 }}
              >
                <Switch checkedChildren="Sì" unCheckedChildren="No" />
              </Form.Item>
              <Form.Item
                name="customizationMaxLength"
                label="Lunghezza max nome (stampa)"
                tooltip="Limite fisico di stampa del nome sul prodotto. Non incide sul prezzo (nome = 5€ fissi)."
                initialValue={12}
                rules={[{ required: !!customizableWatch, type: 'number', min: 1, max: 64 }]}
                style={{ width: 170 }}
              >
                <InputNumber min={1} max={64} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            </Space.Compact>
          </div>

          <Form.Item
            name="sizes"
            label="Taglie e scorte"
            rules={[
              {
                validator: (_r, rows: SizeRow[] | undefined) => {
                  const list = rows ?? [];
                  if (list.some((s) => !s.size || s.size.trim().length === 0)) {
                    return Promise.reject(new Error('Ogni taglia deve avere un nome'));
                  }
                  const seen = new Set<string>();
                  for (const s of list) {
                    const k = s.size.trim().toUpperCase();
                    if (seen.has(k)) {
                      return Promise.reject(new Error(`Taglia duplicata: ${k}`));
                    }
                    seen.add(k);
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <SizeStockField category={categoryWatch} />
          </Form.Item>
        </Form>
      </Modal>

      <CsvImportModal open={csvOpen} onClose={() => setCsvOpen(false)} />
    </Card>
  );
}
